(ns hai.knowledge.components.config-page
  "Configuration page for editing AI model configs / bases / assistants"
  (:require
   [hai.knowledge.api :as api]
   [hai.ui.components.collection.core :as collection]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.library.sources.create :as sources-create]
   [hai.library.sources.table :as sources-table]
   [hai.ui.components :as c]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.components.spinners :as spinners]
   [hai.ui.components.tabs :as tabs]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent-fx]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.deja-fu :as fu]
   [lambdaisland.ornament :as o]
   [reagent.core :as reagent]
   [hai.web-app.util.clipboard :as clipboard]
   [hai.web-app.platform :as platform]
   [hai.web-app.util.scroll :as scroll]))

(o/defstyled form-section :div
  :my-4
  [:.section-title :text-lg :font-semibold :mb-3
   {:color t/--text-primary}]
  [:.section-content :rounded]
  ([{:keys [title]} & children]
   [:<>
    [:div.section-title title]
    (into [:div.section-content] children)]))

(o/defstyled form-field :div
  :mb-3
  [:.label :text-sm :font-medium :mb-1
   {:color t/--text-secondary}]
  [:.input :w-full :p-2 :text-sm :border :rounded
   {:border-color c/--gray-6
    :&:focus {:outline "none"
              :border-color t/--humble-primary}}]
  ([{:keys [label default-value on-change type placeholder]}]
   [:<>
    [:div.label label]
    (case type
      :textarea [:textarea.input
                 {:default-value default-value
                  :on-change #(on-change (-> % .-target .-value))
                  :placeholder placeholder
                  :rows 4}]
      [:input.input
       {:type (or type "text")
        :default-value default-value
        :on-change #(on-change (-> % .-target .-value))
        :placeholder placeholder}])]))

(o/defstyled config-form :div
  [:.actions :flex :gap-3 :justify-end :mt-4]
  ([base]
   (reagent/with-let [form-data (reagent/atom (or base {}))
                      base-id (:ai-model-config/uuid @form-data)]
     [:<>
      [form-section {:title "Basic Information"}
       [form-field
        {:label "Assistant Name"
         :default-value (:ai-model-config/title @form-data)
         :on-change #(swap! form-data assoc :ai-model-config/title %)
         :placeholder "Enter assistant name..."}]
       [form-field
        {:label "Description"
         :type :textarea
         :default-value (:ai-model-config/description @form-data)
         :on-change #(swap! form-data assoc :ai-model-config/description %)
         :placeholder "Describe what this assistant does..."}]]
      [c/Button {:on-click #(>e [:knowledge/update-base base-id @form-data])}
       "Save Configuration"]

      [form-section {:title "Add instructions"}
       (for [prompt (:ai-model-config/prompts @form-data)]
         ^{:key (:ai-prompt/uuid prompt)}
         [form-field
          {:label (:ai-prompt/title prompt)
           :type :textarea
           :default-value (:ai-prompt/content prompt)
           :on-change #(swap! form-data (fn [form]
                                          (update form :ai-model-config/prompts
                                                  (fn [prompts*]
                                                    (map (fn [prompt*]
                                                           (if (= (:ai-prompt/uuid prompt)
                                                                  (:ai-prompt/uuid prompt*))
                                                             (assoc prompt* :ai-prompt/content %)
                                                             prompt*))
                                                         prompts*)))))
           :placeholder "Enter instructions for the AI assistant..."}])]

      [c/Button {:on-click #(doall
                             (for [prompt (:ai-model-config/prompts @form-data)]
                               (>e [:knowledge/update-prompt base-id prompt])))}
       "Save instructions"]])))

(o/defstyled extra-info :div
  :flex :flex-row :gap-4 :rounded :items-center
  [:h3 :mb-3 :font-semibold]
  [:.info-item :p-4 :mb-2 :text-sm
   {:background c/--color-surface}
   [:strong :font-medium
    {:color t/--text-primary}]]
  ([base]
   [:<>
    [:div.info-item
     [:strong "Created: "]
     (when (:ai-model-config/created-at base)
       (fu/format (:ai-model-config/created-at base) "dd MMMM yy"))]]))

(defevent-fx ::attach-sources
  [{:keys [db]} {:keys [base-id from-table to-table]}]
  (let [selection-table (collection/table-from-db db from-table)
        selected (:table/selected selection-table)
        new-source-uuids (mapv :ai-source/uuid selected)
        existing-rows (collection/rows-from-db db to-table)
        existing-source-uuids (mapv :ai-source/uuid existing-rows)
        all-source-uuids (into existing-source-uuids new-source-uuids)]
    {:fetch {:request-id ::knowledge.attach-sources
             :url (api/endpoint-url :ai-model-config :id base-id)
             :method :put
             :body {:sources all-source-uuids}
             :on-success-fx [[:dispatch [:toast/success {:text "Sources attached successfully."}]]
                             [:dispatch [:table/resolve to-table]]]}}))

(def all-available-sources-table-id ::all-available-sources-table)

(defevent-fx ::select-attached-sources
  [{:keys [db]} ?table select-in-table]
  (let [rows (collection/rows-from-db db ?table)]
    {:dispatch [:table/select-rows select-in-table rows]}))

(defn attach-sources-modal
  [base-uuid open? on-close]
  [c/DialogRoot
   {:open open?
    :onOpenChange (fn [open?]
                    (when-not open?
                      (on-close)))}
   [c/DialogContent {:max-width "800px"}
    [c/DialogTitle "Attach Knowledge Sources"]
    [c/dialog-close-x]
    [c/DialogDescription
     "Select sources to attach to this assistant. The assistant will be able to access and reference these sources when answering questions."]
    [c/Box {:py "4"}
     [sources-table/table-wrapper {:id all-available-sources-table-id
                                   :on-load-success (fn [_table]
                                                      (>e [::select-attached-sources ::sources-table/table all-available-sources-table-id]))}]]
    [c/Flex {:justify "end" :gap "3" :mt "4"}
     [c/DialogClose
      [c/Button {:variant "soft"} "Cancel"]]
     [c/Button
      {:variant "solid"
       :disabled (empty? @(<s [:table/selected all-available-sources-table-id]))
       :on-click (fn []
                   (>e [::attach-sources {:base-id base-uuid
                                          :from-table all-available-sources-table-id
                                          :to-table ::sources-table/table}])
                   (on-close))}
      "Attach Sources"]]]])

(defn knowledge-source-section
  [base]
  (reagent/with-let [!modal-open (reagent/atom false)]
    (let [base-uuid (:ai-model-config/uuid base)]
      [c/Box {:mt "6"}
       [c/Heading {:as "h2" :size "4"} "Sources uploaded to this task"]
       [c/Box {:mt "2"}
        [sources-table/table-wrapper {:ai-model-config/uuid base-uuid}]]
       [c/Box {:my "4"}
        [c/Button {:on-click #(reset! !modal-open true)
                   :variant "ghost"}
         [icons/add]
         "Attach existing source"]]
       [attach-sources-modal base-uuid @!modal-open #(reset! !modal-open false)]])))

(defn- private-link-url
  [private-link]
  (when private-link
    (let [token (:private-link/token private-link)]
      (str platform/origin "/app/knowledge/share/" token))))

(defn- private-link-tab-content
  [base]
  (let [base-uuid (:ai-model-config/uuid base)
        loading? (or (= :start @(<s [:fetch/state [::api/create-private-link base-uuid]]))
                     (= :start @(<s [:fetch/state [::api/create-private-link-resolve base-uuid]])))]
    [c/Flex {:gap "4" :direction "column" :p "6"}
     [c/Text {:size "2" :color "gray"}
      "Create a shareable link that grants access to this assistant. Anyone with the link can chat with it."]
     (if-let [private-link (private-link-url (:ai-model-config/private-link base))]
       [c/Flex {:gap "2" :align "center"}
        [c/TextFieldRoot {:style {:width "100%"}
                          :value private-link
                          :on-change (fn [e] (.preventDefault e))}
         [c/TextFieldSlot [icons/outlined-link]]]
        [c/Button
         {:on-click #(do
                       (.writeText (.-clipboard js/navigator) private-link)
                       (>e [:toast/success {:text "Link copied to clipboard!"}]))}
         "Copy"]]
       [c/Box
        [c/Button
         {:on-click #(>e [:knowledge/create-private-link base-uuid])
          :disabled loading?
          :loading loading?}
         "Generate Share Link"]])]))

(o/defstyled channels-section :div
  [:svg :fill-current]
  [:.rt-TabsContent :p-0]
  [:.right-box {:background c/--color-background}
   [:svg :shrink-0 :mt-0.5]]
  ([base]
   (let [base-uuid (:ai-model-config/uuid base)
         published? (:ai-model-config/published? base)]
     [c/Box
      [c/Heading {:as "h2" :size "4"} "Channels"]
      [c/Text {:size "2" :mb "3" :color "gray"}
       "Setup this task to be used anywhere"]
      [c/Box {:my "4"}
       [tabs/tabs-container
        [c/TabsRoot {:defaultValue "link"}
         [c/TabsList
          #_[c/TabsTrigger {:value "slack"}
             [icons/google-drive-icon]
             "Add to Slack"]
          [c/TabsTrigger {:value "link"}
           [icons/outlined-web]
           "Create public link"]
          [c/TabsTrigger {:value "private-link"}
           [icons/outlined-lock]
           "Private share link"]
          #_[c/TabsTrigger {:value "embed"}
             [icons/outlined-chat]
             "Use as chatbot on website"]]
         [c/TabsContent {:value "link"}
          [c/Grid {:columns "66% 1fr"}
           [c/Flex {:gap "4" :direction "column" :p "6"}
            [c/CalloutRoot {:variant "surface" :color (if published? "green" "gray") :size "1"}
             [c/CalloutIcon
              (if published?
                [icons/outlined-task_alt]
                [icons/outlined-info])]
             [c/CalloutText
              (if published?
                "This task is public"
                "This task hasn't been published")]]
            (if published?
              [c/Text {:size "2"}
               "Copy the link to share directly."]
              [c/Text {:size "2"}
               "By publishing this task you will generate a URL that
            anyone can visit to chat with the assistant directly in
            their browser. You’ll also create an embed code for an
            iFrame, which can be used as a chatbot on your website."])

            (when published?
              [c/Flex {:gap "2"}
               [c/TextFieldRoot {:style {:width "100%"}
                                 :value (str "https://assistant.thehumbleai.com/public/" base-uuid)
                                 :on-change (fn [e] (.preventDefault e))}
                [c/TextFieldSlot [icons/outlined-link]]]
               [c/Button
                {:on-click #(do
                              (clipboard/copy-to-clipboard
                               (str "https://assistant.thehumbleai.com/public/" base-uuid)
                               (fn [_]
                                 (>e [:toast/success {:text "Link copied to clipboard!"}]))))}
                "Copy"]])
            [c/Box
             (if published?
               [c/Button
                {:variant "soft"
                 :color "red"
                 :on-click (fn [_]
                             (>e [:entity.shares/unpublish {:entity base
                                                            :on-success-fx [[:dispatch [:entity/resolve {:ai-model-config/uuid base-uuid}]]]}]))
                 :size "2"}
                [icons/outlined-link_off]
                "Unpublish task"]
               [c/Button
                {:on-click (fn [_]
                             (>e [:entity.shares/publish {:entity base
                                                          :on-success-fx [[:dispatch [:entity/resolve {:ai-model-config/uuid base-uuid}]]]}]))
                 :size "2"}
                [icons/outlined-link]
                "Publish task"])]]

           [c/Box {:class "right-box"}
            [c/Flex {:direction "column" :gap "4" :p "6"}
             [c/Flex {:gap "2"}
              [icons/outlined-lock]
              [c/Text {:size "2"}
               "Anyone with the link can chat with your task and ask
               questions about your documents. Avoid sharing publicly
               if they contain sensitive information."]]
             [c/Flex {:gap "2"}
              [icons/outlined-link_off]
              [c/Text {:size "2"}
               "You can unpublish at any time to instantly revoke
               access, including any embedded chatbots on websites."]]
             [c/Flex {:gap "2"}
              [icons/outlined-search]
              [c/Text {:size "2"}
               "Usage is tracked for public tasks, but not who's chatting."]]]]]]
         [c/TabsContent {:value "private-link"}
          [private-link-tab-content base]]]]]])))

(defn sharing
  [base]
  (when base
    [:<>
     [c/Heading {:as "h3" :size "4" :id "sharing"
                 :ref (scroll/scroll-into-view-if-hash-matches "sharing")
                 :style {:scroll-margin-top "80px"}}
      "Sharing"]
     [:h4 "People with access to this resource"]
     [sharing.components/permissions-table base]]))

(o/defstyled start-chat :div
  [:svg :fill-primary]
  ([base]
   [button/dark-small {:text     "Chat with this assistant"
                       :on-click #(>e [:routing/navigate
                                       [:tasks/chats {}
                                        {:ai-model-config (str (:ai-model-config/uuid base))}]])}]))

(defn read-only-message
  [ai-model-config]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading {:label (:ai-model-config/title ai-model-config)}]
   [layout/content
    [c/Box {:max-width "800px"}
     [c/Flex {:direction "column" :gap "4"}
      [c/CalloutRoot {:variant "surface" :color "amber"}
       [c/CalloutIcon [icons/outlined-info]]
       [c/CalloutText
        "You can only start chats with this assistant but not configure it."]]
      [c/Box
       [c/Heading {:size "4" :mb "1"} "Description"]
       [c/Text (:ai-model-config/description ai-model-config)]]]]]
   [layout/sticky-form-bottom-bar
    [start-chat ai-model-config]]])

(defn config-page
  [req]
  (let [ai-model-config-uuid (-> @req :parameters :path :ai-model-config/uuid)
        request-id [:tasks/knowledge-config ai-model-config-uuid]
        fetch-state @(<s [:fetch/state request-id])
        ai-model-config @(<s [:entity {:ai-model-config/uuid ai-model-config-uuid}])]
    (cond
      ;; Loading state
      (= fetch-state :start)
      [c/Flex {:p "8" :justify "center" :align "center"}
       [spinners/pulse-dots]]

      ;; Error state
      (= fetch-state :error)
      [c/Flex {:p "8"}
       [:div "Failed to load assistant"]]

      ;; Entity loaded successfully
      (some? ai-model-config)
      (if (sharing/can-edit? ai-model-config)
        [layout/page
         [layout/breadcrumbs {:breadcrumbs [(:ai-model-config/title ai-model-config)]}]
         [layout/heading {:label (str "Configure " (:ai-model-config/title ai-model-config))}]
         [layout/content
          [extra-info ai-model-config]
          [config-form ai-model-config]
          [c/Box
           [c/Heading {:as "h2" :size "4" :id "upload-sources"
                       :ref (scroll/scroll-into-view-if-hash-matches "upload-sources")
                       :style {:scroll-margin-top "80px"}}
            "Upload new sources to this assistant"]
           [sources-create/source-form {:ai-model-config-uuid (:ai-model-config/uuid ai-model-config)}]
           [c/Flex {:mt "4"
                    :direction "column"
                    :gap "2"}
            [:label
             [c/Checkbox
              {:checked (some? (:ai-model-config/synced-at ai-model-config))
               :style {:margin-top "5px"
                       :margin-right "5px"}
               :on-checked-change (fn [checked?]
                                    (>e [:knowledge/update-assistant-config
                                         (:ai-model-config/uuid ai-model-config)
                                         {:ai-model-config/synced-at (when checked? (js/Date.))}]))}]
             [:span "Re-sync Google-Drive documents daily?"]]
            [:label
             [c/Checkbox
              {:checked (boolean (:ai-model-config/specify-sources ai-model-config))
               :style {:margin-top "5px"
                       :margin-right "5px"}
               :on-checked-change (fn [checked?]
                                    (>e [:knowledge/update-assistant-config
                                         (:ai-model-config/uuid ai-model-config)
                                         {:ai-model-config/specify-sources checked?}]))}]
             [:span "Include citations in responses?"]]]
           [knowledge-source-section ai-model-config]]
          [sharing ai-model-config]
          [channels-section ai-model-config]]
         [layout/sticky-form-bottom-bar
          [start-chat ai-model-config]]]
        [read-only-message ai-model-config])

      ;; Not found (fetch completed but entity is nil)
      :else
      [c/Flex {:p "8"}
       [:div "Assistant not found"]])))
